import React from 'react';
import { Box, Divider, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormikProps } from 'formik';
import LabeledInput from '../Common/LabeledInput';
import CustomDatePicker from '../DatePicker/DatePicker';
import CustomPhoneNumberInput from '../CustomPhoneNumberInput/CustomPhoneNumberInput';
import CustomMultiSelect from '../CustomMultiSelect/CustomMultiSelect';
import StyledButton from '../Common/StyledButton';
import { sanitizeInputName } from '../../utils/userInputUtils';
import { FormValues, StaffMember } from '../../utils/validations/clientDetailsForm';
import { StyledFormHelperText } from '../ClientDetail/ClientDetailForm';

interface AddSingleClientProps {
  formik: FormikProps<FormValues>;
  triggerTextChange: (_e: React.ChangeEvent<HTMLInputElement>) => void;
  handleTagsChange: (_newValue: (string | object)[]) => void;
  handleKeyDown: (_event: React.KeyboardEvent<HTMLInputElement>) => void;
  handleObjectivesBlur: (_e: React.FocusEvent<HTMLDivElement, Element>) => void;
  handleStaffTagsChange: (_newValue: (string | object)[]) => void;
  loadMoreOptions: () => void;
  options: StaffMember[];
  fixedOptions: StaffMember[];
  objectivesInputRef: React.RefObject<HTMLInputElement>;
  addClientLoading: boolean;
  noObjectives?: boolean;
  noCoaches?: boolean;
}

export default function AddSingleClient({
  formik,
  triggerTextChange,
  handleTagsChange,
  handleKeyDown,
  handleObjectivesBlur,
  handleStaffTagsChange,
  loadMoreOptions,
  options,
  fixedOptions,
  objectivesInputRef,
  addClientLoading,
  noObjectives = false,
  noCoaches = false,
}: AddSingleClientProps) {
  const { t } = useTranslation();

  const handleNameChange = (field: string, value: string) => {
    formik.handleChange(field)(sanitizeInputName(value));
  };

  const handlePhoneNumberChange = (pNumber: string) => {
    formik.setFieldValue('phoneNumber', pNumber);
  };

  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - 100);

  const handleDateChange = (field: string, value: string) => {
    formik.setFieldValue(field, value);
    formik.handleChange(field)(value);
    formik.validateField('dob');
  };

  const handleEmailChange = (field: string, value: string) => {
    formik.handleChange(field)(value.toLowerCase());
  };

  const getObjectivesError = () => {
    if (noObjectives) return '';
    if (!formik?.touched?.objectives) return '';
    if (!formik?.errors?.objectives) return '';
    if (!Array.isArray(formik.errors.objectives)) return t(formik.errors.objectives);
    const error = formik.errors.objectives.filter((i) => !!i);
    return error[0];
  };

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <Grid container spacing={2}>
        {/* First Row: Name and Birthday */}
        <Grid item sm={6}>
          <LabeledInput
            label={t('addNewClient.name')}
            id="first-name"
            type="text"
            name="firstName"
            value={formik.values.firstName}
            handleChange={(e) => handleNameChange('firstName', e.target.value)}
            onBlur={formik.handleBlur}
            error={formik.touched.firstName && formik.errors.firstName}
            placeholder={t('addNewClient.name')}
          />
        </Grid>
        <Grid item sm={6}>
          <CustomDatePicker
            handleOneTimeChange={handleDateChange}
            minDate={minDate.toISOString().split('T')[0]}
            maxDate={new Date().toISOString().split('T')[0]}
            label={t('addNewClient.birthday')}
            date={formik.values.dob ?? ''}
            error={formik.touched.dob ? formik.errors.dob : undefined}
          />
        </Grid>

        {/* Second Row: Email and Phone Number */}
        <Grid item sm={6}>
          <LabeledInput
            label={t('addNewClient.email')}
            id="email"
            type="email"
            value={formik.values.email}
            handleChange={(e) => handleEmailChange('email', e.target.value)}
            onBlur={formik.handleBlur}
            error={formik.touched.email && formik.errors.email}
            placeholder={t('addNewClient.email')}
          />
        </Grid>
        <Grid item sm={6}>
          <CustomPhoneNumberInput
            name="phoneNumber"
            value={formik.values.phoneNumber as string}
            handleChange={handlePhoneNumberChange}
            onBlur={formik.handleBlur}
            error={formik.touched.phoneNumber ? formik.errors.phoneNumber : undefined}
          />
        </Grid>

        {/* Objectives */}
        <Grid item xs={12}>
          <CustomMultiSelect
            onTextFieldChange={triggerTextChange}
            options={[]}
            value={formik.values.objectives}
            onChange={handleTagsChange}
            onKeyDown={handleKeyDown}
            onBlur={handleObjectivesBlur}
            label={t('addNewClient.objectives')}
            placeholder={t('addNewClient.objectivesData')}
            inputRef={objectivesInputRef}
            freeSolo
            disabled={noObjectives}
          />
          <StyledFormHelperText error>{t(getObjectivesError())}</StyledFormHelperText>
        </Grid>

        {/* Staff Members */}
        <Grid item xs={12}>
          <CustomMultiSelect
            options={options}
            value={formik.values.staffMembers}
            onChange={handleStaffTagsChange}
            loadMoreOptions={loadMoreOptions}
            label={t('addNewClient.staffMembers')}
            freeSolo={false}
            fixedOptions={fixedOptions}
            withIcon
            disabled={noCoaches}
          />
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Divider sx={{ my: 2 }} />
      <Box display="flex" justifyContent="flex-end">
        <StyledButton loading={addClientLoading} loadingText={t('addNewClient.addingClient')}>
          {t('addNewClient.addClient')}
        </StyledButton>
      </Box>
    </form>
  );
}
